import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { FadeUp } from "@/components/site/Reveal";

const EMAIL = "support@anchorsideglobal.com";

const Section = ({ title, children }) => (
  <div className="border-t border-petrol/15 pt-8 md:pt-10 mt-8 md:mt-10">
    <h2 className="font-serif text-petrol text-2xl md:text-3xl tracking-tight mb-4">
      {title}
    </h2>
    <div className="font-sans text-petrol/80 text-base md:text-lg leading-relaxed max-w-3xl">
      {children}
    </div>
  </div>
);

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, []);

  return (
    <main className="pt-32 md:pt-40 pb-24 md:pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <FadeUp>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.22em] text-verdigris hover:text-petrol transition-colors"
          >
            <ArrowLeft size={16} /> Back to home
          </Link>
        </FadeUp>

        <FadeUp delay={0.05}>
          <h1 className="font-serif text-petrol text-5xl md:text-7xl tracking-tight mt-6 leading-[0.98]">
            Privacy Policy
          </h1>
          <p className="font-sans text-petrol/60 text-sm md:text-base mt-4 tracking-wide">
            Last updated: 22 August 2026
          </p>
          <p className="font-sans text-petrol/80 text-lg md:text-xl leading-relaxed mt-8 max-w-3xl">
            Anchorside Global (<em>"we," "us," "our"</em>) respects your privacy.
            This policy explains what information we collect through this
            website and how we use it.
          </p>
        </FadeUp>

        <FadeUp delay={0.1}>
          <Section title="What we collect">
            When you submit our contact form, we collect your full name, email
            address, firm name, and any message or notes you provide.
          </Section>

          <Section title="Why we collect it">
            We use this information solely to respond to your enquiry and
            discuss how we may be able to support your firm. We do not use it
            for any other purpose.
          </Section>

          <Section title="How we store it">
            Your information is stored securely and is only accessible to the
            Anchorside Global team members handling your enquiry.
          </Section>

          <Section title="Sharing your information">
            We do not sell, rent, or share your information with any third
            party.
          </Section>

          <Section title="How long we keep it">
            We retain your information only for as long as necessary to respond
            to your enquiry, or for as long as you remain in contact with us.
          </Section>

          <Section title="Your rights">
            You may request access to, correction of, or deletion of your
            personal information at any time by emailing us at{" "}
            <a
              href={`mailto:${EMAIL}`}
              data-testid="privacy-email-rights"
              className="text-verdigris hover:text-petrol underline underline-offset-4"
            >
              {EMAIL}
            </a>
            .
          </Section>

          <Section title="Contact us">
            If you have any questions about this policy or how your data is
            handled, contact us at{" "}
            <a
              href={`mailto:${EMAIL}`}
              data-testid="privacy-email-contact"
              className="text-verdigris hover:text-petrol underline underline-offset-4"
            >
              {EMAIL}
            </a>
            .
          </Section>
        </FadeUp>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
